import React from "react";
import { motion } from "framer-motion";
import { RefreshCcw, Calculator, LineChart, ArrowRight } from "lucide-react";
import { FadeUp } from "@/components/motion/Reveal";

const services = [
  {
    icon: RefreshCcw,
    tag: "Life insurance",
    title: "Refinance your policy",
    body: "Like refinancing a mortgage — we shop the market and switch you to a better rate with no gap in coverage. Members save 20–40% and often gain more protection.",
    accent: "terracotta",
  },
  {
    icon: Calculator,
    tag: "Tax planning",
    title: "Keep more of what you earn",
    body: "Proactive strategies for W-2 earners, business owners, and retirees. We look at your return, spot missed opportunities, and build a plan for next April — not the last one.",
    accent: "forest",
  },
  {
    icon: LineChart,
    tag: "Financial consulting",
    title: "A plan that fits your life",
    body: "Retirement, college, big-purchase, or just organizing what you've built. Get a clear-eyed second opinion from a licensed advisor — no product-pushing, no jargon.",
    accent: "sage",
  },
];

const accentClasses = {
  terracotta: "bg-terracotta/10 text-terracotta",
  forest: "bg-forest/10 text-forest",
  sage: "bg-sage/20 text-forest",
};

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 lg:py-32 bg-sand">
      <div className="mx-auto max-w-6xl px-6">
        <FadeUp>
          <div className="inline-flex items-center gap-2 rounded-full border border-forest/15 bg-white px-4 py-1.5 text-[11px] uppercase tracking-[0.16em] font-bold text-forest/70">
            <span className="w-1.5 h-1.5 rounded-full bg-terracotta" /> What we do
          </div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-forest max-w-3xl">
            More than insurance.<br />
            <span className="text-forest/60">Real financial peace of mind.</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mt-5 text-forest/70 text-lg max-w-2xl leading-relaxed">
            One trusted team for the three things that quietly shape your future: cover, taxes, and a plan. No cross-sell, no pressure — just an advisor who knows you.
          </p>
        </FadeUp>

        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`service-card-${s.tag.toLowerCase().replace(/\s+/g, "-")}`}
                className="group rounded-3xl bg-white border border-forest/10 p-7 flex flex-col hover:-translate-y-1 hover:shadow-xl hover:shadow-forest/5 transition-all duration-300"
              >
                <span className={`grid place-items-center w-11 h-11 rounded-xl ${accentClasses[s.accent]}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <p className="mt-5 text-[11px] uppercase tracking-[0.14em] font-bold text-sage">{s.tag}</p>
                <h3 className="mt-1.5 font-serif text-2xl text-forest leading-tight">{s.title}</h3>
                <p className="mt-3 text-forest/65 text-sm leading-relaxed flex-1">{s.body}</p>
                <button
                  type="button"
                  onClick={() => scrollTo("book")}
                  data-testid={`service-cta-${s.tag.toLowerCase().replace(/\s+/g, "-")}`}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-forest group-hover:text-terracotta transition-colors"
                >
                  Book a call <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
